<template>
  <div v-if="applicant">
    <v-form
      ref="form"
      :data="form"
      format="formData"
      :url="$laravel.route('public.applicant.update', {
        applicant: applicant.id,
      })"
      method="post"
      @success="onSuccess"
    >
      <template #default="{ errors }">
        <div class="columns mt-4 mb-0 is-multiline">
          <div class="column is-full-mobile is-full-tablet is-half-desktop">
            <h2
              class="title is-5 mr-5"
              aria-controls="collapse-general-info"
            >
              <b-icon
                class="mr-1"
              />
              {{ $t("property_for_rent").capitalize() }}
            </h2>
            <div class="mt-4">
              <v-description>
                <v-description-item>
                  <template #left>
                    {{ $t('address').capitalize() }}
                  </template>

                  <template #right>
                    <p class="tag is-info is-light is-rounded">
                      {{ applicationFile.property.full_address }}
                    </p>
                  </template>
                </v-description-item>
                <v-description-item>
                  <template #left>
                    {{ $t('city').capitalize() }}
                  </template>

                  <template #right>
                    {{ applicationFile.property.address.city }}
                  </template>
                </v-description-item>
                <v-description-item>
                  <template #left>
                    {{ $t('address_2').capitalize() }}
                  </template>

                  <template #right>
                    {{ applicationFile.property.address.address_2 }}
                  </template>
                </v-description-item>
                <v-description-item>
                  <template #left>
                    {{ $t('postal_code').capitalize() }}
                  </template>

                  <template #right>
                    {{ applicationFile.property.address.postal_code }}
                  </template>
                </v-description-item>
                <v-description-item>
                  <template #left>
                    {{ $t('asked_rent').capitalize() }}
                  </template>

                  <template #right>
                    {{ applicationFile.rent }}$
                  </template>
                </v-description-item>

                <v-description-item>
                  <template #left>
                    {{ $t('lease_term_start_date') }}
                  </template>

                  <template #right>
                    {{ $dayjs(applicationFile.lease_term_start_date).format('LL') }}
                  </template>
                </v-description-item>
                <v-description-item>
                  <template #left>
                    {{ $t('lease_term_end_date') }}
                  </template>

                  <template #right>
                    {{ $dayjs(applicationFile.lease_term_end_date).format('LL') }}
                  </template>
                </v-description-item>

                <v-description-item>
                  <template #left>
                    {{ $t('heating_included') }}
                  </template>

                  <template #right>
                    <p>
                      {{
                        applicationFile.heating_included
                          ? $t("yes").capitalize()
                          : $t("no").capitalize()
                      }}
                    </p>
                  </template>
                </v-description-item>

                <v-description-item>
                  <template #left>
                    {{ $t('lighting_included') }}
                  </template>

                  <template #right>
                    <p>
                      {{
                        applicationFile.lighting_included
                          ? $t("yes").capitalize()
                          : $t("no").capitalize()
                      }}
                    </p>
                  </template>
                </v-description-item>
              </v-description>

              <div class="mb-4 mt-4">
                <div class="">
                  <d-card>
                    <d-card-row>
                      <div class="mb-4">
                        <v-input
                          v-model="form.social_insurance_number"
                          name="social_insurance_number"
                          :label="$t('social_insurance_number').capitalize()"
                          :placeholder="$t('social_insurance_number').capitalize()"
                        />
                        <d-notification
                          type="is-info is-light"
                          class="mt-4"
                        >
                          <p class="is-italic">
                            {{ $t('social_insurance_number_description') }}
                          </p>
                        </d-notification>
                      </div>
                    </d-card-row>
                  </d-card>
                </div>
              </div>
            </div>
          </div>
          <div class="column is-full-mobile is-full-tablet is-half-desktop">
            <h2
              class="title is-5 mr-5 mb-4"
              aria-controls="collapse-general-form-1"
            >
              {{ $t("tenant_candidate_title").capitalize() }}
            </h2>
            <div class="mt-4">
              <d-card>
                <d-card-row>
                  <div class="mb-4">
                    <v-input
                      v-model="form.first_name"
                      name="first_name"
                      :label="$t('first_name')"
                      disabled
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.last_name"
                      name="last_name"
                      :label="$t('last_name')"
                      disabled
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-date
                      v-model="form.birthday"
                      name="birthday"
                      :label="$t('birthday')"
                      required
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.phone_number"
                      type="tel"
                      name="phone_number"
                      :placeholder="$t('phone').capitalize()"
                      :label="$t('phone').capitalize()"
                      required
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.email"
                      type="email"
                      name="email"
                      :label="$t('email').capitalize()"
                      disabled
                    />
                  </div>
                </d-card-row>
              </d-card>
              <div class="mb-4 columns">
                <div class="column">
                  <d-card>
                    <d-card-row>
                      <div class="mb-4">
                        <v-input-switch
                          v-model="form.owe_money"
                          :true-value="true"
                          :false-value="false"
                          name="owe_money"
                          :label="$t('owe_money')"
                        />
                      </div>
                      <div class="mb-4">
                        <v-input-switch
                          v-model="form.previous_insurer_refusal"
                          :true-value="true"
                          :false-value="false"
                          name="previous_insurer_refusal"
                          :label="$t('previous_insurer_refusal')"
                        />
                      </div>
                    </d-card-row>
                  </d-card>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-full-mobile is-full-tablet is-half-desktop">
            <h2
              class="title is-5 mr-5"
              aria-controls="collapse-general-current-address"
            >
              {{ $t("current_address").capitalize() }}
            </h2>
            <div class="mt-4">
              <d-card>
                <d-card-row>
                  <div class="mb-4">
                    <div class="field required">
                      <label class="label">{{
                        $t("address").capitalize()
                      }}</label>
                      <div class="control is-clearfix">
                        <input
                          ref="currentAddress1Field"
                          :value="form.current_address_address"
                          name="current_address_address"
                          class="input"
                          :class="{ 'is-danger': currentAddressError(errors) }"
                          required
                          @keydown.enter.prevent
                          @input="($event) => form.current_address_address = $event.target.value"
                        >

                        <p
                          v-if="currentAddressError(errors)"
                          class="help is-danger"
                        >
                          {{ currentAddressError(errors) }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.current_address_unit_number"
                      name="current_address_unit_number"
                      :label="$t('unit_number').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.current_address_city"
                      name="current_address_city"
                      :label="$t('city').capitalize()"
                      required
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.current_address_postal_code"
                      name="current_address_postal_code"
                      :label="$t('postal_code').capitalize()"
                      required
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.current_address_rent_per_month"
                      name="current_address_rent_per_month"
                      :label="$t('rent_per_month').capitalize()"
                      required
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-switch
                      v-model="form.current_address_heating_included"
                      :true-value="true"
                      :false-value="false"
                      name="current_address_heating_included"
                      :label="$t('heating_included')"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-switch
                      v-model="form.current_address_lighting_included"
                      :true-value="true"
                      :false-value="false"
                      name="current_address_lighting_included"
                      :label="$t('lighting_included')"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-date
                      v-model="form.current_address_occupation_period_start_date"
                      name="current_address_occupation_period_start_date"
                      :label="$t('occupation_period_start_date')"
                      required
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-date
                      v-model="form.current_address_occupation_period_end_date"
                      name="current_address_occupation_period_end_date"
                      :label="$t('occupation_period_end_date')"
                      required
                    />
                  </div>
                </d-card-row>
                <d-card-row>
                  <h3 class="title is-6 mr-5 mb-4">
                    {{ $t("current_owner").capitalize() }}
                  </h3>
                  <div class="mb-4">
                    <v-input
                      v-model="form.current_address_owner_full_name"
                      name="current_address_owner_full_name"
                      :label="$t('owner_full_name')"
                      required
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.current_address_owner_phone_number"
                      name="current_address_owner_phone_number"
                      :label="$t('phone')"
                      required
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.current_address_owner_email"
                      name="current_address_owner_email"
                      :label="$t('email')"
                      required
                    />
                  </div>
                </d-card-row>
              </d-card>
            </div>
          </div>
          <div class="column is-full-mobile is-full-tablet is-half-desktop">
            <h2
              class="title is-5 mr-5"
              aria-controls="collapse-general-previous-address"
            >
              {{ $t("previous_address").capitalize() }}
            </h2>
            <div class="mt-4">
              <d-card>
                <d-card-row>
                  <div class="mb-4">
                    <div class="field">
                      <label class="label">{{
                        $t("address").capitalize()
                      }}</label>
                      <div class="control is-clearfix">
                        <input
                          ref="previousAddress1Field"
                          :value="form.previous_address_address"
                          name="previous_address_address"
                          class="input"
                          :class="{ 'is-danger': previousAddressError(errors) }"
                          @keydown.enter.prevent
                          @input="($event) => form.previous_address_address = $event.target.value"
                        >

                        <p
                          v-if="previousAddressError(errors)"
                          class="help is-danger"
                        >
                          {{ previousAddressError(errors) }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.previous_address_unit_number"
                      name="previous_address_unit_number"
                      :label="$t('unit_number').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.previous_address_city"
                      name="previous_address_city"
                      :label="$t('city').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.previous_address_postal_code"
                      name="previous_address_postal_code"
                      :label="$t('postal_code').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.previous_address_rent_per_month"
                      name="previous_address_rent_per_month"
                      :label="$t('rent_per_month').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-switch
                      v-model="form.previous_address_heating_included"
                      :true-value="true"
                      :false-value="false"
                      name="previous_address_heating_included"
                      :label="$t('heating_included')"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-switch
                      v-model="form.previous_address_lighting_included"
                      :true-value="true"
                      :false-value="false"
                      name="previous_address_lighting_included"
                      :label="$t('lighting_included')"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-date
                      v-model="form.previous_address_occupation_period_start_date"
                      name="previous_address_occupation_period_start_date"
                      :label="$t('occupation_period_start_date')"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-date
                      v-model="form.previous_address_occupation_period_end_date"
                      name="previous_address_occupation_period_end_date"
                      :label="$t('occupation_period_end_date')"
                    />
                  </div>
                </d-card-row>
                <d-card-row>
                  <h3 class="title is-6 mr-5 mb-4">
                    {{ $t("previous_owner").capitalize() }}
                  </h3>
                  <div class="mb-4">
                    <v-input
                      v-model="form.previous_address_owner_full_name"
                      name="previous_address_owner_full_name"
                      :label="$t('owner_full_name')"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.previous_address_owner_phone_number"
                      name="previous_address_owner_phone_number"
                      :label="$t('phone')"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.previous_address_owner_email"
                      name="previous_address_owner_email"
                      :label="$t('email')"
                    />
                  </div>
                </d-card-row>
              </d-card>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <v-input-switch
              v-model="form.is_unemployed"
              :true-value="true"
              :false-value="false"
              name="is_unemployed"
              :label="$t('is_unemployed')"
            />
          </div>
        </div>
        <div class="columns is-multiline">
          <div class="column is-full-mobile is-full-tablet is-half-desktop">
            <h2
              class="title is-5 mr-5"
              aria-controls="collapse-general-employee-1"
            >
              {{ $t("current_employee_one").capitalize() }}
            </h2>
            <div class="mt-4">
              <d-card>
                <d-card-row>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_1_business_name"
                      name="employee_1_business_name"
                      :label="$t('business_name').capitalize()"
                      :required="isCurrentEmployeeRequired"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_1_phone_number"
                      name="employee_1_phone_number"
                      :label="$t('phone').capitalize()"
                      :required="isCurrentEmployeeRequired"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_1_phone_number_extension"
                      name="employee_1_phone_number_extension"
                      :label="$t('phone_number_extension').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_1_address"
                      name="employee_1_address"
                      :label="$t('address').capitalize()"
                      :required="isCurrentEmployeeRequired"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_1_position_held"
                      name="employee_1_position_held"
                      :label="$t('position_held').capitalize()"
                      :required="isCurrentEmployeeRequired"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_1_superior_name"
                      name="employee_1_superior_name"
                      :label="$t('superior_name').capitalize()"
                      :required="isCurrentEmployeeRequired"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_1_annual_revenue"
                      name="employee_1_annual_revenue"
                      type="number"
                      :label="$t('annual_revenue').capitalize()"
                      :required="isCurrentEmployeeRequired"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_1_hourly_rate"
                      name="employee_1_hourly_rate"
                      type="number"
                      :label="$t('hourly_rate').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-select
                      v-model="form.employee_1_job_status_legal"
                      name="employee_1_job_status_legal"
                      :label="$t('job_status_legal')"
                      :required="isCurrentEmployeeRequired"
                    >
                      <option :value="null">
                        {{ $t("select").capitalize() }}
                      </option>
                      <option
                        v-for="(jobStatusLegal, key) in $laravel.employeeJobStatusLegalEnum"
                        :key="key"
                        :value="jobStatusLegal.value"
                      >
                        {{ jobStatusLegal.label }}
                      </option>
                    </v-input-select>
                  </div>
                  <div class="mb-4">
                    <v-input-select
                      v-model="form.employee_1_job_status_schedule"
                      name="employee_1_job_status_schedule"
                      :label="$t('job_status_schedule')"
                      :required="isCurrentEmployeeRequired"
                    >
                      <option :value="null">
                        {{ $t("select").capitalize() }}
                      </option>
                      <option
                        v-for="(jobStatusSchedule, key) in $laravel.employeeJobStatusScheduleEnum"
                        :key="key"
                        :value="jobStatusSchedule.value"
                      >
                        {{ jobStatusSchedule.label }}
                      </option>
                    </v-input-select>
                  </div>
                  <div class="mb-4">
                    <v-input-date
                      v-model="form.employee_1_employed_since"
                      name="employee_1_employed_since"
                      :label="$t('employed_since').capitalize()"
                      :required="isCurrentEmployeeRequired"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_1_hours_per_week"
                      type="number"
                      name="employee_1_hours_per_week"
                      :label="$t('hours_per_week').capitalize()"
                      :required="isCurrentEmployeeRequired"
                    />
                  </div>
                </d-card-row>
              </d-card>
            </div>
          </div>
          <div class="column is-full-mobile is-full-tablet is-half-desktop">
            <h2
              class="title is-5 mr-5"
              aria-controls="collapse-general-employee-2"
            >
              {{ $t("current_employee_two").capitalize() }}
            </h2>
            <div class="mt-4">
              <d-card>
                <d-card-row>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_2_business_name"
                      name="employee_2_business_name"
                      :label="$t('business_name').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_2_phone_number"
                      name="employee_2_phone_number"
                      :label="$t('phone').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_2_phone_number_extension"
                      name="employee_2_phone_number_extension"
                      :label="$t('phone_number_extension').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_2_address"
                      name="employee_2_address"
                      :label="$t('address').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_2_position_held"
                      name="employee_2_position_held"
                      :label="$t('position_held').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_2_superior_name"
                      name="employee_2_superior_name"
                      :label="$t('superior_name').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_2_annual_revenue"
                      name="employee_2_annual_revenue"
                      type="number"
                      :label="$t('annual_revenue').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_2_hourly_rate"
                      name="employee_2_hourly_rate"
                      type="number"
                      :label="$t('hourly_rate').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input-select
                      v-model="form.employee_2_job_status_legal"
                      name="employee_2_job_status_legal"
                      :label="$t('job_status_legal')"
                    >
                      <option :value="null">
                        {{ $t("select").capitalize() }}
                      </option>
                      <option
                        v-for="(jobStatusLegal, key) in $laravel.employeeJobStatusLegalEnum"
                        :key="key"
                        :value="jobStatusLegal.value"
                      >
                        {{ jobStatusLegal.label }}
                      </option>
                    </v-input-select>
                  </div>
                  <div class="mb-4">
                    <v-input-select
                      v-model="form.employee_2_job_status_schedule"
                      name="employee_2_job_status_schedule"
                      :label="$t('job_status_schedule')"
                    >
                      <option :value="null">
                        {{ $t("select").capitalize() }}
                      </option>
                      <option
                        v-for="(jobStatusSchedule, key) in $laravel.employeeJobStatusScheduleEnum"
                        :key="key"
                        :value="jobStatusSchedule.value"
                      >
                        {{ jobStatusSchedule.label }}
                      </option>
                    </v-input-select>
                  </div>
                  <div class="mb-4">
                    <v-input-date
                      v-model="form.employee_2_employed_since"
                      name="employee_2_employed_since"
                      :label="$t('employed_since').capitalize()"
                    />
                  </div>
                  <div class="mb-4">
                    <v-input
                      v-model="form.employee_2_hours_per_week"
                      name="employee_2_hours_per_week"
                      type="number"
                      :label="$t('hours_per_week').capitalize()"
                    />
                  </div>
                </d-card-row>
              </d-card>
            </div>
          </div>
        </div>


        <div class="columns">
          <div class="column">
            <p class="is-italic is-size-7">
              J’atteste que ces informations sont complètes et véridiques et que je n’ai en aucune façon travesti, falsifié ou omis des faits qui pourraient invalider ce formulaire ou influencer la décision du locateur. Je suis avisé qu’une fausse déclaration peut entraîner l’annulation du bail et constituer une fraude en vertu de l’article 380 du Code criminel, lequel prévoit notamment une peine de prison. J’autorise le locateur et ses représentants à obtenir ou échanger des renseignements personnels avec tout agent de renseignements personnels, institutions financières, employeurs, propriétaires ou autres institutions et personnes ci-haut mentionnés aux fins d’établir ma solvabilité et ma capacité à respecter les obligations du bail. J’autorise également le locateur personnellement ou ses intermédiaires à obtenir ou échanger tout renseignement pertinent auprès d’un propriétaire passé, présent et futur, employeur, institution financière, agence de recouvrement ou autre advenant l’existence d’une créance et/ou d’un jugement en faveur de l’une ou l’autre de ces personnes. J’autorise la CORPIQ, à titre d’agent de renseignements personnels, à recueillir et à communiquer au locateur ou à son représentant tout renseignement personnel, incluant ceux qu’elle détiendrait déjà sur moi en vertu d’un consentement antérieur. Ce présent consentement à la cueillette et à la communication de renseignements personnels est valide durant 14 jours civils à compter de la date de signature. Si un bail est signé, le consentement demeure valide jusqu’à trois ans après l’échéance de ce bail uniquement aux fins de recouvrement d’un loyer dû ou, si un jugement a été rendu par le tribunal, pour recouvrer toute autre créance relative au bail.
            </p>
            <h2 class="title is-5 mt-6 mb-4">
              Le candidat locatiare et sa caustion doivent montrer une pièce d'identité fiable avec photo
            </h2>

            <div class="mb-4 is-flex is-align-items-start is-justify-content-space-between gap-2">
              <label class="checkbox">
                <b-checkbox
                  v-model="isAcceptedCredit"
                  size="is-large"
                />
              </label>
              <p class="is-size-7">
                Je confirme que les informations fournies par le présent formulaire sont exactes et j’accepte qu’une enquête de
                pré-location soit effectuée pour cette demande de location avec ProprioEnquête et je comprends que
                ProprioEnquête accedera à mon dossier de crédit.
              </p>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <h2
              class="title is-5 mr-5"
              aria-controls="collapse-general-employee-2"
            >
              {{ $t("signature").capitalize() }}
            </h2>
            <div class="mt-4">
              <div
                class="signature mb-4"
                :class="{'is-danger': errors && errors.signature && !isSignatureFilled}"
              >
                <img
                  v-if="signature"
                  :src="signature"
                  class="mb-4"
                >
              </div>
              <p
                v-if="errors && errors.signature && !isSignatureFilled"
                class="help is-danger mt-4 mb-4"
              >
                {{ errors.signature[0] }}
              </p>
              <div>
                <button
                  class="button is-light"
                  type="button"
                  @click="updateSignature"
                >
                  <i class="mdi mdi-pencil-square" />
                  <span>
                    <span v-if="signature">

                      {{ $t('update_signature') }}
                    </span>
                    <span v-else>
                      {{ $t('add_signature') }}
                    </span>
                  </span>
                </button>
              </div>
            </div>
            <b-modal
              v-model="isShownSignatureModal"
              :width="600"
              @close="isShownSignatureModal = false"
            >
              <header
                class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
              >
                <div
                  class="is-flex is-full is-align-content-space-between"
                >
                  <p class="modal-card-title title mb-2">
                    {{ $t("update_signature") }}
                  </p>
                </div>
              </header>
              <div class="modal-card">
                <section class="modal-card-body">
                  <v-signature @success="handleSignature" />
                </section>
              </div>
            </b-modal>
          </div>
        </div>

        <button
          class="button is-primary mt-4 mb-4"
          type="submit"
          :disabled="!isAcceptedCredit"
        >
          {{ $t('submit_application') }}
        </button>
        <a
          v-if="$laravel && $laravel.user && $roleCan([$laravel.role_admin])"
          :href="$laravel.route('api.application-files.applicants.credit_pdf', {
            applicationFile: applicationFile.id,
            applicant: applicant.id,
            property: applicationFile.property.id
          })"
          class="button is-light mt-4 mb-4"
          target="_blank"
        >
          {{ $t('download_pdf') }}
        </a>
      </template>
    </v-form>
  </div>
</template>

<script>
import { fillFields } from "@/services/helpers";

import BaseDescriptionList from "@/shared/components/BaseDescriptionList.vue";
import BaseDescriptionListItem from "@/shared/components/BaseDescriptionListItem.vue";
import SignaturePad from "./SignaturePad.vue";

export default {
  components: {
    "v-description": BaseDescriptionList,
    "v-description-item": BaseDescriptionListItem,
    "v-signature": SignaturePad,
  },
  props: {
    applicant: {
      default: null,
      type: Object
    },
    applicationFile: {
      default: null,
      type: Object
    },
  },
  data () {
    return {
      currentAutocomplete: null,
      previousAutoComplete: null,
      isAcceptedCredit:false,
      showPreviousAddressForm:false,
      showEmployee2Form:false,
      form: {
        application_file_id: this.applicationFile ? this.applicationFile.id : null,
        first_name: null,
        last_name: null,
        email: null,
        birthday: null,
        phone_number: null,
        social_insurance_number: null,
        owe_money: false,
        previous_insurer_refusal: false,
        current_address_address: null,
        current_address_unit_number: null,
        current_address_city: null,
        current_address_postal_code: null,
        current_address_rent_per_month: null,
        current_address_heating_included: false,
        current_address_lighting_included: false,
        current_address_occupation_period_start_date: null,
        current_address_occupation_period_end_date: null,
        current_address_owner_full_name: null,
        current_address_owner_phone_number: null,
        current_address_owner_email: null,
        previous_address_address: null,
        previous_address_unit_number: null,
        previous_address_city: null,
        previous_address_postal_code: null,
        previous_address_rent_per_month: null,
        previous_address_heating_included: false,
        previous_address_lighting_included: false,
        previous_address_occupation_period_start_date: null,
        previous_address_occupation_period_end_date: null,
        previous_address_owner_full_name: null,
        previous_address_owner_phone_number: null,
        previous_address_owner_email: null,
        is_unemployed: false,
        employee_1_business_name: null,
        employee_1_phone_number: null,
        employee_1_phone_number_extension: null,
        employee_1_address: null,
        employee_1_position_held: null,
        employee_1_superior_name: null,
        employee_1_annual_revenue: null,
        employee_1_hourly_rate: null,
        employee_1_job_status_schedule: null,
        employee_1_job_status_legal: null,
        employee_1_employed_since: null,
        employee_1_hours_per_week: null,
        employee_2_business_name: null,
        employee_2_phone_number: null,
        employee_2_phone_number_extension: null,
        employee_2_address: null,
        employee_2_position_held: null,
        employee_2_superior_name: null,
        employee_2_annual_revenue: null,
        employee_2_hourly_rate: null,
        employee_2_job_status_schedule: null,
        employee_2_job_status_legal: null,
        employee_2_employed_since: null,
        employee_2_hours_per_week: null,
        signature: null,
        step: "credit",
      },
      isShownSignatureModal: false,
      signature: null,
      isSignatureFilled:false
    };
  },
  computed: {
    isCurrentEmployeeRequired() {
      return ! this.form.is_unemployed;
    },
  },
  created () {
    this.form = fillFields(this.form, this.applicant);

    if (this.applicant.signature) {
      this.form.signature = null;
      this.signature = this.applicant.signature.url;
      this.isSignatureFilled = true;
    }

  },
  mounted(){
    const currentAddress1Field = this.$refs.currentAddress1Field;
    const previousAddress1Field = this.$refs.previousAddress1Field;

    this.currentAutocomplete = new google.maps.places.Autocomplete(
      currentAddress1Field,
      {
        fields: ["address_components"],
        types: ["address"],
        componentRestrictions: { country: "ca" },
      }
    );

    this.currentAutocomplete.addListener("place_changed", this.fillCurrentAddress);

    this.previousAutoComplete = new google.maps.places.Autocomplete(
      previousAddress1Field,
      {
        fields: ["address_components"],
        types: ["address"],
        componentRestrictions: { country: "ca" },
      }
    );

    this.previousAutoComplete.addListener("place_changed", this.fillPreviousAddress);


  },
  methods: {
    currentAddressError(errors) {
      const error = _.get(errors, "current_address_address", null);
      return error && error[0] ? error[0] : false;
    },
    previousAddressError(errors) {
      const error = _.get(errors, "previous_address_address", null);
      return error && error[0] ? error[0] : false;
    },
    fillCurrentAddress() {
      if (!this.currentAutocomplete) {
        return;
      }

      const place = this.currentAutocomplete.getPlace();

      let address1 = "";

      const newForm = _.cloneDeep(this.form);

      if (!place.address_components) {
        return;
      }

      for (const component of place.address_components) {
        const componentType = component.types[0];
        switch (componentType) {
        case "street_number": {
          address1 = `${component.long_name} ${address1}`;
          newForm.current_address_address = address1;
          break;
        }
        case "route": {
          address1 += component.long_name;
          newForm.current_address_address = address1;
          break;
        }
        case "postal_code": {
          newForm.current_address_postal_code = `${component.long_name}`;
          break;
        }
        case "postal_code_prefix": {
          newForm.current_address_postal_code = `${component.long_name}`;
          break;
        }
        case "locality":
          newForm.current_address_city = component.long_name;
          break;
        }
      }
      this.$nextTick(() => {
        // Force value change
        this.form = newForm;
      });
    },
    fillPreviousAddress() {

      if (!this.previousAutoComplete) {
        return;
      }

      const place = this.previousAutoComplete.getPlace();

      let address1 = "";

      const newForm = _.cloneDeep(this.form);

      if (!place.address_components) {
        return;
      }

      for (const component of place.address_components) {
        const componentType = component.types[0];
        switch (componentType) {
        case "street_number": {
          address1 = `${component.long_name} ${address1}`;
          newForm.previous_address_address = address1;
          break;
        }
        case "route": {
          address1 += component.long_name;
          newForm.previous_address_address = address1;
          break;
        }
        case "postal_code": {
          newForm.previous_address_postal_code = `${component.long_name}`;
          break;
        }
        case "postal_code_prefix": {
          newForm.previous_address_postal_code = `${component.long_name}`;
          break;
        }
        case "locality":
          newForm.previous_address_city = component.long_name;
          break;
        }
      }
      this.$nextTick(() => {
        this.form = newForm;
      });
    },
    onSuccess() {
      this.$emit("success");
    },
    handleSignature(signatureData) {
      this.form.signature = signatureData;
      this.signature = signatureData;
      this.isShownSignatureModal = false;
      this.isSignatureFilled = true;
    },
    updateSignature() {
      this.isShownSignatureModal = true;
    }
  }
};
</script>
<style scoped>
.signature {
  border: 1px solid #d1d5dc;
  background-color: #f7f7f7;
  border-radius: 10px;
  height: 200px;
  width: 400px;
}
.w-full {
  width: 100% !important;
}
.signature.is-danger {
  border:solid 1px #ef4444 !important;
}
</style>
